import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

export const Card = ({ title, subtitle, children, action, testId, className = "" }) => (
  <section className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`} data-testid={testId}>
    <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-base md:text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

export const Empty = ({ text = "No claims in this period" }) => (
  <div className="py-10 text-center text-sm text-gray-400" data-testid="empty-state">{text}</div>
);

export const Change = ({ value, suffix = "%", invert = false, neutral = false }) => {
  if (value === null || value === undefined) return <span className="text-xs text-gray-400" data-testid="change-na">vs previous: —</span>;
  const good = invert ? value <= 0 : value >= 0;
  const Icon = value === 0 ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  const color = neutral || value === 0 ? "text-gray-600" : good ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {value > 0 ? "+" : ""}{value}{suffix} vs previous
    </span>
  );
};

export const StatCard = ({ label, value, change, changeSuffix, invert, neutral, testId }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-3xl font-bold text-gray-900 mt-2 tabular-nums">{value}</p>
    <div className="mt-2"><Change value={change} suffix={changeSuffix} invert={invert} neutral={neutral} /></div>
  </div>
);

export const BarList = ({ items, nameKey = "name", countKey = "count", max, onSelect, selected, testIdPrefix }) => {
  const top = max ?? Math.max(...items.map((i) => i[countKey]), 1);
  return (
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li key={item[nameKey] + idx}>
          <button
            type="button"
            onClick={onSelect ? () => onSelect(item) : undefined}
            className={`w-full text-left rounded-md px-2 py-1.5 transition-colors ${onSelect ? "hover:bg-blue-50 cursor-pointer" : "cursor-default"} ${selected === item[nameKey] ? "bg-blue-50" : ""}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-${idx}` : undefined}
          >
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-800 truncate">{item[nameKey]}</span>
              <span className="tabular-nums text-gray-600 ml-3 whitespace-nowrap">
                {item[countKey]}{item.share !== undefined && <span className="text-gray-400"> · {item.share}%</span>}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded mt-1 overflow-hidden">
              <div className="h-full bg-blue-500 rounded transition-[width] duration-500" style={{ width: `${(item[countKey] / top) * 100}%` }} />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};
